import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, ShieldCheck, Leaf, Clock3, CalendarCheck } from "lucide-react";
import { CONTACT, waLink, IMG, BG } from "../lib/data";
import { scrollToId } from "../lib/scroll";
import { useLang } from "../lib/i18n";
import { SectionBg } from "./SectionBg";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.15 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
  }),
};

const CHIP_ICONS = [ShieldCheck, Leaf, Clock3];

export default function Hero() {
  const { lang, t, a } = useLang();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 110]);
  const scale = useTransform(scrollY, [0, 700], [1.02, 1.12]);

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
      <SectionBg src={BG.hero} opacity={0.42} testid="hero-bg-image" />
      <div className="absolute inset-0 arch-grid pointer-events-none" />
      <div className="absolute -top-40 -left-40 w-[560px] h-[560px] rounded-full bg-gold/10 blur-[160px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[480px] h-[480px] rounded-full bg-skyish/10 blur-[160px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-20 lg:pt-40 lg:pb-28 grid lg:grid-cols-12 gap-14 items-center w-full">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.05 }}
            className="inline-flex items-center gap-3 rounded-full border border-gold/30 bg-gold/5 px-4 py-2"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-mint opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-mint" />
            </span>
            <span className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-gold">
              {t.hero.eyebrow}
            </span>
          </motion.div>

          <h1 data-testid="hero-headline" className="font-display font-extrabold tracking-tight leading-[1.02] text-[clamp(2.6rem,7.2vw,6rem)] mt-8">
            {t.hero.lines.map(([text, style], i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  custom={i}
                  variants={line}
                  initial="hidden"
                  animate="show"
                  className={`block ${style === "gold" ? "text-gold" : style === "outline" ? "text-outline" : "text-white"}`}
                >
                  {text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75 }}
            className="text-slate-400 text-base sm:text-lg leading-relaxed max-w-xl mt-7"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="flex flex-wrap items-center gap-4 mt-9"
          >
            <Link
              data-testid="hero-book-online-button"
              to="/book"
              className="inline-flex items-center gap-2.5 rounded-full bg-gold text-ink font-semibold px-7 py-4 text-sm sm:text-base hover:bg-white transition-colors duration-300"
            >
              <CalendarCheck size={18} strokeWidth={2.3} />
              {a.nav.book}
            </Link>
            <a
              data-testid="hero-whatsapp-button"
              href={waLink(lang === "en" ? "Hi OGO! I'd like to book a service." : "హాయ్ OGO! సర్వీస్ బుక్ చేయాలనుకుంటున్నాను.")}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2.5 rounded-full border border-white/20 text-white font-semibold px-7 py-4 text-sm sm:text-base hover:border-gold hover:text-gold transition-colors duration-300"
            >
              <MessageCircle size={18} strokeWidth={2.3} />
              {t.hero.book}
            </a>
            <a
              data-testid="hero-call-button"
              href={CONTACT.tel}
              className="inline-flex items-center gap-2.5 rounded-full border border-white/20 text-white font-semibold px-7 py-4 text-sm sm:text-base hover:border-gold hover:text-gold transition-colors duration-300"
            >
              <Phone size={17} strokeWidth={2.3} />
              {CONTACT.phoneDisplay}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="flex flex-wrap gap-x-7 gap-y-3 mt-10"
          >
            {t.hero.chips.map((label, i) => {
              const Icon = CHIP_ICONS[i];
              return (
                <span key={label} className="inline-flex items-center gap-2 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-slate-400">
                  <Icon size={14} className="text-mint" /> {label}
                </span>
              );
            })}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative mx-auto max-w-[380px] lg:max-w-none"
          >
            <motion.div style={{ y, scale }} className="arch overflow-hidden border border-white/15 h-[440px] sm:h-[540px] lg:h-[620px] relative">
              <img
                src={IMG.hero}
                alt="OGO technician deep-cleaning a home floor"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0E] via-transparent to-transparent opacity-80" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.3 }}
              className="absolute -bottom-6 -left-4 sm:-left-10 rounded-2xl border border-white/10 bg-[#12141C]/85 backdrop-blur-xl px-5 py-4 shadow-2xl"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-mint flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-mint" /> {t.hero.openNow}
              </p>
              <p className="text-white font-semibold text-sm mt-1.5">{CONTACT.hours[lang]}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: -16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.45 }}
              className="absolute -top-5 -right-2 sm:-right-6 rounded-full border border-gold/40 bg-ink/90 backdrop-blur px-5 py-3"
            >
              <button data-testid="hero-services-scroll" onClick={() => scrollToId("services")} className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                {t.hero.six}
              </button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
